<template>
  <div class="chat" :class="{ light }">
    <div class="chat__head">
      <chat-header
        hide-actions
        :campaign="campaign"
        :channel="channel"
        :client="client"
        :last-message="lastMessage"
        :agentID="agentId"
        :favorite="isFavorite"
      />
    </div>
    <div class="chat__body">
      <chat-conversation
        :clientId="activeClientHistoryId"
        :messages="messages"
      />
    </div>
    <transition name="fade">
      <div v-if="showAssignedAgent" class="assigned-warning">
        {{ assignedAgent }}
      </div>
    </transition>
    <div class="chat__footer">
      <chat-shortcuts />
      <chat-multimedia @send-message="prepareMessage" />
      <v-emoji-picker
        class="emoji"
        :emojisByRow="15"
        v-show="showEmojis"
        @select="selectEmoji"
      />
      <chat-preview
        :message="previewedMessage"
        @confirm="confirmMessage"
        @cancel="cancelPreview"
      />
      <chat-input
        ref="chatInput"
        :disabled="historyInputDisabled || isPreviewShowing || isBlocked"
        :client-id="clientId"
        :selected-campaign-id="activeCampaign"
        :channel-type="channelType"
        :show-cancel-button="true"
        @send-message="prepareMessage"
        @cancelled="lockInput"
      />
      <input-lock v-if="isBlocked">
        <template #button>
          <div class="block-warning">
            {{ textWarning }}
          </div>
        </template>
      </input-lock>
      <input-lock
        v-else
        @unlock-clicked="unlockInput"
        :button-is-visible="historyInputDisabled"
      />
    </div>
    <campaigns-modal @confirm-campaign="openDatabank" />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { VEmojiPicker } from 'v-emoji-picker';
import {
  CHAT_EMOJI_TOGGLE,
  CHAT_MULTIMEDIA_TOGGLE,
  CHAT_SEND_MESSAGE,
  SHORTCUTS_TOGGLE,
} from '@/eventTypes';
import EventBus from '@/eventBus';
import checkConversationStatus from '@/graphql/queries/checkConversationStatus.gql';
import CampaignsModal from './CampaignsModal.vue';

export default {
  components: {
    ChatConversation: () => import('@/components/ChatConversation.vue'),
    ChatHeader: () => import('@/components/ChatHeader.vue'),
    ChatInput: () => import('@/components/ChatInput.vue'),
    ChatMultimedia: () => import('@/components/ChatMultimedia.vue'),
    ChatPreview: () => import('@/components/ChatPreview.vue'),
    ChatShortcuts: () => import('@/components/ChatShortcuts.vue'),
    InputLock: () => import('@/components/InputLock.vue'),
    VEmojiPicker,
    CampaignsModal,
  },
  data() {
    return {
      previewedMessage: null,
      historyInputDisabled: true,
      showEmojis: false,
      showAssignedAgent: false,
      interval: null,
      currentAgent: null,
    };
  },
  computed: {
    ...mapState({
      activeCampaign: (state) => state.currentCampaign,
      conversationsHistory: (state) => state.conversationsHistory,
      activeClientHistoryId: (state) => state.activeClientHistoryId,
    }),
    ...mapGetters([
      'activeConversationHistory',
      'getUserCampaigns',
      'userInfo',
      'theme',
      'getUserCampaigns',
    ]),
    channel() {
      return this.activeConversationHistory && this.activeConversationHistory.conversation
        ? this.activeConversationHistory.conversation.channel ?? {}
        : {};
    },
    campaign() {
      return this.activeConversationHistory && this.activeConversationHistory.conversation
        ? this.activeConversationHistory.conversation.campaign ?? {}
        : {};
    },
    currentConversation() {
      return this.activeConversationHistory
        ? this.activeConversationHistory.conversation ?? {}
        : {};
    },
    channelId() {
      return this.channel.id;
    },
    channelType() {
      return this.channel.channelType.key ?? '';
    },
    client() {
      return this.activeConversationHistory
        ? this.activeConversationHistory.client ?? {}
        : {};
    },
    clientId() {
      return this.client.id || '';
    },
    messages() {
      return this.activeConversationHistory
        ? this.activeConversationHistory.messages ?? []
        : [];
    },
    lastMessage() {
      if (this.messages.length > 0) {
        return this.messages[this.messages.length - 1];
      }

      return {};
    },
    agentId() {
      return this.activeConversationHistory ? this.activeConversationHistory.agentId : '';
    },
    isFavorite() {
      let isFavorite = false;
      const activeConversation = this.activeConversationHistory;

      if (activeConversation) {
        const { favorites } = activeConversation.client;

        isFavorite = favorites.some(
          (fave) => fave.campaign === activeConversation.conversation.campaign.id
            && fave.user === activeConversation.agentId,
        );
      }

      return isFavorite;
    },
    isBlocked() {
      const activeConversation = this.activeConversationHistory;
      if (activeConversation) {
        const { blocked } = activeConversation.client;
        return blocked && blocked.some(
          (block) => block.channel === activeConversation.conversation.channel.id
            && block.user === activeConversation.agentId,
        );
      }

      return false;
    },
    isPreviewShowing() {
      return this.previewedMessage !== null;
    },
    light() {
      return this.theme === 'light';
    },
    textWarning() {
      if (this.isBlocked) {
        return this.$t('dialog.block-warning');
      }

      return '';
    },
    assignedAgent() {
      return this.$t('dialog.assigned-warning', this.currentAgent);
    },
  },
  watch: {
    clientId() {
      this.lockInput();
    },
  },
  created() {
    EventBus.$on(CHAT_EMOJI_TOGGLE, (opened) => {
      this.showEmojis = opened;
    });
  },
  beforeDestroy() {
    clearInterval(this.interval);
  },
  destroyed() {
    EventBus.$off(CHAT_EMOJI_TOGGLE);
  },
  methods: {
    ...mapMutations(['setCurrentCampaign']),
    async unlockInput() {
      await this.getAssignedAgent();
      if (this.currentAgent) {
        this.showAssignedAgent = true;
        this.interval = setInterval(() => {
          this.showAssignedAgent = false;
        }, 5000);
      } else {
        this.setCurrentCampaign(null);

        this.$modal.show('campaigns-modal');
      }
    },
    openDatabank(campaign) {
      this.setCurrentCampaign(campaign.id);

      this.historyInputDisabled = false;
      EventBus.$emit(CHAT_MULTIMEDIA_TOGGLE, true);
    },
    lockInput() {
      this.historyInputDisabled = true;
      EventBus.$emit(CHAT_MULTIMEDIA_TOGGLE, false);
      EventBus.$emit(SHORTCUTS_TOGGLE, false);
    },
    hasPreview(type) {
      return !['text', 'tText'].includes(type);
    },
    prepareMessage(basicMsg) {
      this.previewedMessage = null;

      const msg = basicMsg.message || basicMsg;

      const payload = {
        ...msg,
        tempId: this.uuidv4(),
        typeUser: 'User',
        user: {
          id: this.userInfo.id,
        },
      };

      const message = this.buildMessage(
        this.clientId,
        this.channelId,
        this.activeCampaign,
        payload,
      );

      if (this.hasPreview(message.type)) {
        this.previewedMessage = {
          ...message,
          filename: msg.filename,
        };
      } else {
        this.sendMessage(message);
      }
    },
    confirmMessage() {
      this.sendMessage(this.previewedMessage);
    },
    sendMessage(msg) {
      EventBus.$emit(CHAT_SEND_MESSAGE, msg, true);
      this.cancelPreview();
    },
    cancelPreview() {
      this.previewedMessage = null;
    },
    selectEmoji(emoji) {
      this.$refs.chatInput.addEmoji(emoji.data);
    },
    async getAssignedAgent() {
      const { data: response } = await this.$apollo.query({
        query: checkConversationStatus,
        variables: { client: this.clientId, channel: this.channelId },
        fetchPolicy: 'no-cache',

      });

      const { agent } = response.checkConversationStatus;

      this.currentAgent = agent;
    },
  },
};
</script>

<style lang="scss">
.emoji {
  width: 100% !important;

  .container-emoji {
    max-height: 200px;
  }
}

.block-warning {
  padding: 10px;
  margin: 0 20px 8px;
  border-radius: 4px;
  background-color: lighten($alert-red-alt, 20%);
  font-weight: bold;
  color: $alert-red;
  text-align: center;
  transition: all ease 0.5s;
  width: 95%;
}

.assigned-warning {
  padding: 10px;
  margin: 0 20px 8px;
  border-radius: 4px;
  background-color: lighten($alert-green, 30%);
  font-weight: bold;
  color: darken($alert-green, 20%);
  text-align: center;
  transition: all ease 0.5s;
  width: 95%;
}
</style>
